import React, { useState } from "react";
import { useParams } from "react-router";
import { Link } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { Helmet } from "react-helmet";
import chunk from "lodash.chunk";

import { City, CountyCollection, PalJobsPageCollection } from "types";
import { contentfulClient } from "config/contentfulClient";
import { slugify, slugToText, getState, capitalize } from "utils/helpers";

import { US_STATES } from "utils/states";

import Page404 from "containers/Responses/Page404";

import PageHeader from "components/PageHeader";
import Row from "components/Row";
import Col from "components/Col";
import Button from "components/Button";
import Container from "components/Container";

import { GET_CITIES, GET_LOCAL_JOBS_PAGE } from "./gql";

interface ParamsProps {
  county: string;
  state: string;
}

interface Data {
  countyCollection: CountyCollection;
}

interface PageData {
  palJobsPageCollection: PalJobsPageCollection;
}

const County: React.FC = () => {
  const params = useParams<ParamsProps>();
  const [redirect, setRedirect] = useState<boolean>(false);

  const { data, loading } = useQuery<Data>(GET_CITIES, {
    client: contentfulClient,
    variables: {
      where: {
        county_contains: slugToText(params.county),
      },
    },
    onCompleted: (data) => {
      const state = US_STATES.find(
        (obj) =>
          obj.abbreviation ===
          data?.countyCollection.items[0].linkedFrom.stateCollection.items[0].state.toUpperCase()
      );

      if (params.state !== slugify(state?.name)) {
        setRedirect(true);
      }
    },
  });

  const { data: pageData, loading: pageLoading } = useQuery<PageData>(
    GET_LOCAL_JOBS_PAGE,
    {
      client: contentfulClient,
      variables: {
        where: {
          pageName: "countyJobs",
        },
      },
    }
  );

  const filteredState = getState(params.state);

  const replaceKeywords = (value: string | undefined) => {
    if (!value) return "";
    return value
      .replaceAll("%COUNTY%", slugToText(params.county))
      .replaceAll("%STATE%", filteredState!.name);
  };

  if (loading || pageLoading) {
    return null;
  }

  if (redirect) {
    return <Page404 />;
  }

  const cities = data?.countyCollection.items[0].citiesCollection.items || [];
  const sortedCities = cities.slice().sort((a, b) => {
    if (a.city < b.city) return -1;
    if (a.city > b.city) return 1;
    return 0;
  });

  const locations =
    sortedCities.length < 3
      ? [sortedCities]
      : chunk(sortedCities, sortedCities.length / 3);

  const {
    metaTitle,
    metaDescription,
    metaImage,
    headerHeading,
    headerImage,
    headerSubheading,
    contentCopy,
    contentHeading,
  } = pageData?.palJobsPageCollection.items[0] ?? {};

  return (
    <>
      <Helmet>
        <title>{replaceKeywords(metaTitle)}</title>
        <meta name="title" content={replaceKeywords(metaTitle)}></meta>
        <meta
          name="description"
          content={replaceKeywords(metaDescription)}
        ></meta>

        <meta property="og:type" content="website"></meta>
        <meta
          property="og:url"
          content="https://www.papa.com/papa-pal-qualified-application/"
        ></meta>
        <meta property="og:title" content={replaceKeywords(metaTitle)}></meta>
        <meta
          property="og:description"
          content={replaceKeywords(metaDescription)}
        ></meta>
        <meta property="og:image" content={`https://${metaImage?.url}`}></meta>

        <meta property="twitter:card" content="summary_large_image"></meta>
        <meta
          property="twitter:url"
          content="https://www.papa.com/papa-pal-qualified-application/"
        ></meta>
        <meta
          property="twitter:title"
          content={replaceKeywords(metaTitle)}
        ></meta>
        <meta
          property="twitter:description"
          content={replaceKeywords(metaDescription)}
        ></meta>
        <meta
          property="twitter:image"
          content={`https://${metaImage?.url}`}
        ></meta>
      </Helmet>

      <div className="jobs">
        <PageHeader>
          <Row>
            <Col md={5}>
              <h1>{replaceKeywords(headerHeading)}</h1>
              <hr />
              <p>{replaceKeywords(headerSubheading)}</p>

              <Button to="/pals/signup">Apply Now</Button>
            </Col>

            <img
              src={headerImage?.url}
              alt="Papa"
              className="page-header__image"
            />
          </Row>
        </PageHeader>

        <Container className="content">
          <Row>
            <Col md={{ span: 8, offset: 2 }}>
              <div className="overview text-center">
                <h2>{replaceKeywords(contentHeading)}</h2>
                <p>{replaceKeywords(contentCopy)}</p>
              </div>
            </Col>
          </Row>

          <h3 className="underline">Cities</h3>

          <div className="jobs__cities">
            <Row>
              {locations.map((item, i) => (
                <Col key={i} md={4}>
                  {item.map((location: City, i: number) => (
                    <Link
                      key={i}
                      to={`/pal-local-jobs/${params.state}/${
                        params.county
                      }/${slugify(location.city)}`}
                    >
                      {location.city}
                    </Link>
                  ))}
                </Col>
              ))}
            </Row>
          </div>

          <div className="application-form">
            <Row>
              <Col md={{ span: 6, offset: 3 }} className="text-center">
                <h4 className="mb-20">
                  Papa is growing in {capitalize(filteredState!.name)}. Ready to
                  join us? Get started below.
                </h4>
                <Button to="/pals/signup">Apply Now</Button>
              </Col>
            </Row>
          </div>
        </Container>
      </div>
    </>
  );
};

export default County;
